// ============================================
// AREASDELUSUARIO.JS — en que areas trabaja cada persona
// ============================================
// POR QUE EXISTE (2026-09-24): el responsable de un expediente no lo elige nadie a mano.
// Lo elige el motor de flujos entre los MIEMBROS DEL AREA dueña del paso en curso. Hasta
// hoy esa membresia solo se ponia en el arranque del servidor, escrita a mano en
// `initDb.js`, y NO habia ninguna pantalla para verla ni cambiarla.
//
// Lo que costo: Ricardo estaba sembrado en 3 de las 8 areas y Javier en NINGUNA, asi que
// el algoritmo no podia asignarle un expediente a Javier ni queriendo. 203 de 315
// expedientes acabaron con responsable distinto de su creador. No se podia arreglar desde
// la aplicacion porque no habia donde tocarlo.
//
// Ahora se toca aqui, y el arranque ya no opina: la siembra solo actua sobre una
// instalacion virgen.
//
// ESTO REPARTE TRABAJO DE VERDAD, no es una etiqueta. Por eso el texto de la pantalla lo
// dice con todas las letras y por eso escribir exige ser administrador (el candado esta
// en el servidor, en routes/areas.js).

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';
import { authHeaders } from '../utils/storage';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';

export default function AreasDelUsuario({ usuarioId, puedeEditar = false, onCambio = null }) {
  const [areas, setAreas] = useState([]);
  const [mias, setMias] = useState({});      // { [codigo]: { es_lider } }
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/areas`, { headers: authHeaders() }).then(x => x.json());
      const lista = (r && (r.areas || r.data)) || [];
      setAreas(Array.isArray(lista) ? lista : []);

      // Los miembros van por area: se pregunta a cada una y se queda con las de este usuario.
      const mapa = {};
      await Promise.all((Array.isArray(lista) ? lista : []).map(async (a) => {
        try {
          const m = await fetch(`${API_BASE}/api/areas/${a.codigo}/miembros`, { headers: authHeaders() })
            .then(x => x.json());
          const miembros = (m && (m.miembros || m.data)) || [];
          const yo = miembros.find(x => Number(x.usuario_id ?? x.id) === Number(usuarioId));
          if (yo) mapa[a.codigo] = { es_lider: !!yo.es_lider };
        } catch { /* un area que falle no tumba el resto */ }
      }));
      setMias(mapa);
    } catch (e) { setError('No se pudieron leer las areas.'); }
    finally { setCargando(false); }
  }, [usuarioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const alternar = async (codigo) => {
    if (!puedeEditar || guardando) return;
    setGuardando(codigo); setError(null);
    const estaba = !!mias[codigo];
    try {
      const resp = estaba
        ? await fetch(`${API_BASE}/api/areas/${codigo}/miembros/${usuarioId}`, { method: 'DELETE', headers: authHeaders() })
        : await fetch(`${API_BASE}/api/areas/${codigo}/miembros`, {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ usuario_id: usuarioId, es_lider: false }),
          });
      if (!resp.ok) {
        const j = await resp.json().catch(() => null);
        setError((j && j.error) || (resp.status === 403 ? 'Solo un administrador puede cambiar las areas.' : 'No se pudo guardar.'));
        return;
      }
      setMias(m => {
        const n = { ...m };
        if (estaba) delete n[codigo]; else n[codigo] = { es_lider: false };
        return n;
      });
      onCambio && onCambio();
    } catch { setError('No se pudo conectar.'); }
    finally { setGuardando(null); }
  };

  if (cargando) {
    return <div style={{ fontSize: 12, color: GRIS_CLARO, fontFamily: PP, padding: '8px 0' }}>Cargando áreas…</div>;
  }
  if (!areas.length) return null;

  const cuantas = Object.keys(mias).length;

  return (
    <div style={{ fontFamily: PP, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#2e3338', marginBottom: 2 }}>
        Áreas de trabajo
        <span style={{ fontWeight: 500, color: GRIS_CLARO }}> · {cuantas === 0 ? 'ninguna' : cuantas}</span>
      </div>
      <div style={{ fontSize: 11.5, color: GRIS_CLARO, marginBottom: 8, lineHeight: 1.45 }}>
        De aquí sale <strong>a quién se le asignan los expedientes</strong>: cada paso del flujo pertenece
        a un área, y el responsable se elige entre quienes están en ella. Si alguien no está en
        ninguna, nunca le tocará un expediente de forma automática.
      </div>

      {cuantas === 0 && (
        <div style={{
          background: '#fff8ed', border: '1px solid #fce5b8', borderRadius: 8,
          padding: '7px 10px', fontSize: 12, color: '#8a5a1b', marginBottom: 8,
        }}>
          No está en ninguna área: el sistema no puede asignarle expedientes solo.
        </div>
      )}
      {error && (
        <div style={{
          background: '#fdecec', border: '1px solid #f3c9c9', borderRadius: 8,
          padding: '7px 10px', fontSize: 12, color: '#8a2b2b', marginBottom: 8,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {areas.map(a => {
          const dentro = !!mias[a.codigo];
          const lider = dentro && mias[a.codigo].es_lider;
          return (
            <button
              key={a.codigo}
              onClick={() => alternar(a.codigo)}
              disabled={!puedeEditar || guardando === a.codigo}
              title={puedeEditar
                ? (dentro ? `Quitar a esta persona de ${a.nombre || a.codigo}` : `Meter a esta persona en ${a.nombre || a.codigo}`)
                : 'Solo un administrador puede cambiar las áreas'}
              style={{
                padding: '8px 12px', minHeight: 40, borderRadius: 999,
                border: `1px solid ${dentro ? AZUL : BORDE}`,
                background: dentro ? AZUL : '#fff',
                color: dentro ? '#fff' : GRIS,
                fontFamily: PP, fontSize: 12.5, fontWeight: dentro ? 700 : 500,
                cursor: puedeEditar && guardando !== a.codigo ? 'pointer' : 'default',
                opacity: guardando === a.codigo ? 0.6 : 1,
              }}
            >
              {dentro ? '✓ ' : ''}{a.nombre || a.codigo}{lider ? ' ★' : ''}
            </button>
          );
        })}
      </div>
      {Object.values(mias).some(m => m.es_lider) && (
        <div style={{ fontSize: 11, color: GRIS_CLARO, marginTop: 6 }}>
          ★ = líder del área (el comodín cuando no hay nadie más disponible).
        </div>
      )}
    </div>
  );
}
